import { useEffect, useState } from "react";

interface VersionCheckResult {
  isNewVersionAvailable: boolean;
  currentVersion: string;
  latestVersion: string | null;
  checkForUpdates: () => Promise<void>;
  forceReload: () => void;
}

const CURRENT_VERSION = process.env.REACT_APP_VERSION || "1.0.0";
const CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

// localStorage keys
const STORAGE_KEYS = {
  NEW_VERSION_AVAILABLE: "newVersionAvailable",
  LATEST_VERSION: "latestVersion",
} as const;

export const useVersionCheck = (): VersionCheckResult => {
  const [isNewVersionAvailable, setIsNewVersionAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  // Helper function to detect network errors
  const isNetworkError = (error: unknown): boolean => {
    return (
      error instanceof TypeError &&
      (error.message.includes("Failed to fetch") ||
        error.message.includes("NetworkError") ||
        error.message.includes("Load failed")) // Safari-specific error message
    );
  };

  const checkForUpdates = async (): Promise<void> => {
    // Skip check if user is offline
    if (!navigator.onLine) {
      console.debug("Skipping version check - user is offline");
      return;
    }

    try {
      // Method 1: Check meta tag in index.html
      const response = await fetch(`/index.html?t=${Date.now()}`, {
        cache: "no-cache",
        headers: {
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
      });

      if (response.ok) {
        const html = await response.text();

        // Extract version from meta tag or build info
        const versionMatch = html.match(/data-version="([^"][a-f0-9]+)"/);
        const extractedVersion = versionMatch ? versionMatch[1] : null;

        if (extractedVersion && extractedVersion !== CURRENT_VERSION) {
          setLatestVersion(extractedVersion);
          setIsNewVersionAvailable(true);

          // Store in localStorage to persist across page navigation
          localStorage.setItem(STORAGE_KEYS.NEW_VERSION_AVAILABLE, "true");
          localStorage.setItem(STORAGE_KEYS.LATEST_VERSION, extractedVersion);
        } else {
          localStorage.setItem(STORAGE_KEYS.NEW_VERSION_AVAILABLE, "false");
        }
      }
    } catch (error) {
      // Check if it's a network error
      if (isNetworkError(error)) {
        // Silently fail for network errors - user is likely offline or has connectivity issues
        console.debug("Version check skipped due to network unavailability");
        return;
      }

      console.warn("Failed to check for app updates:", error);

      // Fallback: Check if main JS chunk returns 404 (indicates new build)
      // Only attempt if we're still online and it wasn't a network error
      if (navigator.onLine) {
        try {
          const scripts = Array.from(document.querySelectorAll('script[src*="/static/js/"]'));
          if (scripts.length > 0) {
            const mainScript = scripts[0] as HTMLScriptElement;
            const scriptResponse = await fetch(mainScript.src, {
              method: "HEAD",
              cache: "no-cache",
            });

            if (scriptResponse.status === 404) {
              setIsNewVersionAvailable(true);
              localStorage.setItem(STORAGE_KEYS.NEW_VERSION_AVAILABLE, "true");
            }
          }
        } catch (fallbackError) {
          // Only log if it's not a network error
          if (!isNetworkError(fallbackError)) {
            console.warn("Fallback version check also failed:", fallbackError);
          } else {
            console.debug("Fallback version check skipped due to network unavailability");
          }
        }
      }
    }
  };

  const forceReload = (): void => {
    // Clear all caches and reload
    localStorage.removeItem(STORAGE_KEYS.NEW_VERSION_AVAILABLE);
    localStorage.removeItem(STORAGE_KEYS.LATEST_VERSION);

    // Clear service worker cache if available
    if ("serviceWorker" in navigator && "caches" in window) {
      caches
        .keys()
        .then((names) => {
          names.forEach((name) => caches.delete(name));
        })
        .finally(() => {
          window.location.reload();
        });
    } else {
      window.location.reload();
    }
  };

  useEffect(() => {
    // Check if we already detected a new version
    const storedNewVersion = localStorage.getItem(STORAGE_KEYS.NEW_VERSION_AVAILABLE);
    const storedLatestVersion = localStorage.getItem(STORAGE_KEYS.LATEST_VERSION);

    if (storedNewVersion === "true") {
      setIsNewVersionAvailable(true);
      setLatestVersion(storedLatestVersion);
    }

    // Initial check
    checkForUpdates();

    // Set up periodic checking
    const interval = setInterval(checkForUpdates, CHECK_INTERVAL);

    // Check when window gains focus (user returns to tab)
    const handleFocus = () => {
      checkForUpdates();
    };

    // Check when user comes back online
    const handleOnline = () => {
      console.debug("Network connection restored, checking for updates");
      checkForUpdates();
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleOnline);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  return {
    isNewVersionAvailable,
    currentVersion: CURRENT_VERSION,
    latestVersion,
    checkForUpdates,
    forceReload,
  };
};
